import { type Health, type Ladder, type ProposalStatus, type Scope } from './types';

/** What Statuspage shows for each Health (TDD §1.6). Operators know the page's words. */
export const HEALTH_LABEL: Record<Health, string> = {
  ok: 'Operational',
  impaired: 'Degraded Performance',
  degraded: 'Partial Outage',
  failed: 'Major Outage',
  maintenance: 'Under Maintenance',
};

/** Severity order, mildest first. Maintenance is not a severity; it sorts after ok for display only. */
const SEVERITY: Record<Health, number> = {
  ok: 0,
  maintenance: 1,
  impaired: 2,
  degraded: 3,
  failed: 4,
};

export function severity(health: Health): number {
  return SEVERITY[health];
}

export function worstOf(healths: Health[]): Health {
  return healths.reduce<Health>((worst, h) => (SEVERITY[h] > SEVERITY[worst] ? h : worst), 'ok');
}

export function isWorse(a: Health, b: Health): boolean {
  return SEVERITY[a] > SEVERITY[b];
}

/** Ladder steps sorted ascending as numbers, e.g. {3, 6, 12}. */
export function ladderSteps(ladder: Ladder): { step: number; health: Health }[] {
  return Object.entries(ladder)
    .map(([k, health]) => ({ step: Number(k), health }))
    .sort((a, b) => a.step - b.step);
}

/** The implied Health for a fail streak: the highest ladder step reached, or ok (§4.5). */
export function impliedHealth(ladder: Ladder, failStreak: number): Health {
  let implied: Health = 'ok';
  for (const { step, health } of ladderSteps(ladder)) {
    if (failStreak >= step) implied = health;
  }
  return implied;
}

export const SCOPE_LABEL: Record<Scope, string> = {
  'us-prod': 'US Production',
  'us-uat': 'US UAT',
  'eu-prod': 'EU Production',
  'eu-uat': 'EU UAT',
};

export const SCOPE_SHORT: Record<Scope, string> = {
  'us-prod': 'US Prod',
  'us-uat': 'US UAT',
  'eu-prod': 'EU Prod',
  'eu-uat': 'EU UAT',
};

export const PROPOSAL_STATUS_LABEL: Record<ProposalStatus, string> = {
  open: 'Awaiting approval',
  approved: 'Publishing',
  rejected: 'Rejected',
  superseded: 'Superseded',
  withdrawn: 'Withdrawn',
  published: 'Published',
  publish_failed: 'Publish failed',
};

/** A proposal's direction, for wording: escalation, de-escalation or recovery. */
export function proposalKind(
  from: Health,
  to: Health,
): 'recovery' | 'escalation' | 'de-escalation' {
  if (to === 'ok') return 'recovery';
  return isWorse(to, from) ? 'escalation' : 'de-escalation';
}

/** Groups are a display convention: Import and Export nest their CSV and CEDE children (TDD §1.2). */
export function groupOf(capabilityId: string): string | null {
  const [head, tail] = capabilityId.split('.');
  if (!tail) return null;
  if (head === 'import') return 'Import';
  if (head === 'export') return 'Export';
  return null;
}
